<script setup lang="ts">
import merge from 'lodash-es/merge';
import type { ListItemModel } from '../../../pages/cms/modules/articles/_runtime/services/ArticlesService';
import ArticlesService from '../../../pages/cms/modules/articles/_runtime/services/ArticlesService';
import type { ListItemModel as CategoryListItemModel } from '../../../pages/cms/modules/articles/_runtime/services/ArticleCategoriesService';
import ArticleCategoriesService from '../../../pages/cms/modules/articles/_runtime/services/ArticleCategoriesService';

defineOptions({
    name: 'WidgetArticlesListSettings'
});

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const { $t } = useLocalization();

const module = ref('articles');
const defaults = ref<any>({
    articlesPage: null,
    selectedCategories: [],
    defaultCategoryId: null,
    quantity: 3,
    priority: false
});
const categoryId = ref<number>(null);
const categories = ref<CategoryListItemModel[]>([]);
const related = ref<ListItemModel[]>([]);

const form = computed<any>(() =>
{
    return props.modelValue;
});

function triggerModel(): void
{
    emit('update:modelValue', form.value);
}

async function fetchCategory(id: number): Promise<CategoryListItemModel>
{
    const response = await ArticleCategoriesService.fetchAutocompleteModel(id);

    return response || null;
}

async function searchCategories(query: string = '', limit: number = 10): Promise<CategoryListItemModel[]>
{
    return await ArticleCategoriesService.getAutocompleteList(query, limit);
}

async function addCategory(): Promise<void>
{
    if (categoryId.value == null)
        return;

    const response = await ArticleCategoriesService.fetchAutocompleteModel(categoryId.value);

    if (categories.value.find(x => x.id === response.id) === undefined &&
      form.value.settings.selectedCategories.find((x: any) => x === response.id) === undefined)
    {
        categories.value.push(response);
        form.value.settings.selectedCategories.push(response.id);
    }

    categoryId.value = null;
}

function removeCategory(id: number): void
{
    categories.value = categories.value.filter(x => x.id != id);
    form.value.settings.selectedCategories = form.value.settings.selectedCategories.filter((x: any) => x != id);
}

async function loadCategories(id: number): Promise<boolean>
{
    if (id === 0)
        return true;

    try
    {
        categories.value = await ArticleCategoriesService.getSelectedFromWidget(id);
    }
    catch (ex)
    {
        return false;
    }

    return true;
}

async function loadRelated(categoryId: number): Promise<boolean>
{
    if (categoryId === null)
        categoryId = 0;

    try
    {
        related.value = await ArticlesService.getListByCategoryId(categoryId);
    }
    catch (ex)
    {
        return false;
    }

    return true;
}

watch(() => form.value.settings, () =>
{
    loadRelated(defaults.value.defaultCategoryId);
    triggerModel();
}, { deep: true });

onBeforeMount(async (): Promise<void> =>
{
    form.value.settings = merge(defaults.value, form.value.settings);
    loadCategories(form.value.id);
});
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Wybór strony]]]')" :invalid-feedback="form.$errors.first('settings.articlesPage')" :state="form.$errors.state('settings.articlesPage')">
        <EditoPageSelector v-model="form.settings.articlesPage" :module="module" label="" :preview="false" class="mb-0" />
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Wybierz kategorię]]]')">
        <div class="d-flex flex-column flex-md-row">
            <div class="flex-grow-1">
                <IdeoAutocomplete
                    v-model="categoryId"
                    name="categoryId"
                    track-by="id"
                    :placeholder="$t('[[[zacznij pisać aby wyszukać kategorię...]]]')"
                    :fetch="(id: number) => fetchCategory(id)"
                    :search="(query: string, limit: number) => searchCategories(query, limit)"
                >
                    <template #selected="{ option }">
                        <div class="text-wrap">
                            {{ option.name }}
                        </div>
                    </template>
                    <template #option="{ option }">
                        <div class="text-wrap">
                            {{ option.name }}
                        </div>
                    </template>
                </IdeoAutocomplete>
            </div>
            <div class="d-flex align-items-stretch">
                <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addCategory()">
                    <i class="fas fa-plus" />
                </button>
            </div>
        </div>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Powiązane kategorię]]]')">
        <div class="list-group">
            <div v-for="item in categories" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                <span class="me-2">
                    <i class="fad fa-fw fa-browser text-primary" />
                </span>
                <span class="me-auto">
                    <span class="d-block" :title="$t('[[[Powiązana kategoria]]]')">
                        <b>{{ item.name }}</b>
                    </span>
                </span>
                <span class="d-flex align-items-center">
                    <IdeoButton :id="`btn-delete-related-${item.id}`" variant="danger" size="sm" pill :title="$t('[[[Usuń kategorię]]]')">
                        <i class="fas fa-trash-alt" />
                    </IdeoButton>
                    <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-related-${item.id}`" :value="item.id" @confirm="removeCategory(item.id)" />
                </span>
            </div>
            <div v-if="categories.length == 0" class="list-group-item list-group-item-light text-center">
                {{ $t('[[[Nie wybrano żadnej kategorii]]]') }}
            </div>
        </div>
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Wybierz domyślną kategorię]]]')" :invalid-feedback="form.$errors.first('settings.defaultCategoryId')" :state="form.$errors.state('settings.defaultCategoryId')">
        <IdeoFormSelect v-model="form.settings.defaultCategoryId" name="defaultCategoryId" :options="categories" value-field="id" text-field="name" />
    </IdeoFormGroup>

    <IdeoFormGroup>
        <IdeoFormCheckbox v-model="form.settings.priority">
            {{ $t('[[[Artykuły ze statusem PRIORYTET]]]') }}
        </IdeoFormCheckbox>
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Powiązane artykuły]]]')">
        <div class="list-group">
            <div v-for="item in related" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                <span class="me-2">
                    <i class="fad fa-fw fa-browser text-primary" />
                </span>
                <span class="me-auto">
                    <span class="d-block" :title="$t('[[[Powiązany artykuł]]]')">
                        <b>{{ item.title }}</b>
                    </span>
                </span>
            </div>
            <div v-if="related.length == 0" class="list-group-item list-group-item-light text-center">
                {{ $t('[[[Brak powiązań]]]') }}
            </div>
        </div>
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Liczba artykułów]]]')" :invalid-feedback="form.$errors.first('settings.quantity')" :state="form.$errors.state('settings.quantity')">
        <IdeoFormInput v-model="form.settings.quantity" type="number" name="quantity" />
    </IdeoFormGroup>
</template>
